/**
 * CO-995 — the choices behind the /payroll/table Excel export: what the panel offers, what the server's
 * contract calls it (PayrollTableExportInput, PayrollTableColumnKeys) and what is remembered per browser.
 * Nothing here knows about pay. Every figure comes from the server re-reading the page's own rows.
 */

export type ColumnKey =
  | 'name' | 'staffNumber' | 'department' | 'position' | 'country'
  | 'fixedPay' | 'variablePay' | 'adjustments' | 'overtimePay' | 'deductions' | 'netPay'
  | 'regularOvertimeHours' | 'premiumOvertimeHours'
  | 'paymentStatus' | 'bankName' | 'iban' | 'labourCard';

export type ExportLayout = 'register' | 'summaryAndRegister' | 'oneSheetPerGroup';
export type ExportGroupBy = 'none' | 'department' | 'position' | 'country' | 'paymentStatus';
export type ExportSortBy = 'name' | 'staffNumber' | 'netPayHighToLow';
export type ExportPreset = 'paySummary' | 'fullBreakdown' | 'bankSheet';
export type ColumnFamily = 'employee' | 'pay' | 'overtimeHours' | 'payment';

export interface ExportOptions {
  columns: ColumnKey[];
  layout: ExportLayout;
  groupBy: ExportGroupBy;
  sortBy: ExportSortBy;
  showSubtotals: boolean;
}

export const COLUMN_FAMILIES: { family: ColumnFamily; columns: ColumnKey[] }[] = [
  { family: 'employee', columns: ['name', 'staffNumber', 'department', 'position', 'country'] },
  { family: 'pay', columns: ['fixedPay', 'variablePay', 'adjustments', 'overtimePay', 'deductions', 'netPay'] },
  { family: 'overtimeHours', columns: ['regularOvertimeHours', 'premiumOvertimeHours'] },
  { family: 'payment', columns: ['paymentStatus', 'bankName', 'iban', 'labourCard'] },
];

const CANONICAL_ORDER: ColumnKey[] = COLUMN_FAMILIES.flatMap((f) => f.columns);

/** What this company's export may offer. The server applies the same two rules whatever it is sent. */
export interface ColumnAccess {
  /** The labour card is a UAE WPS field. */
  labourCardApplies: boolean;
  /** The company's "Allow bank details in payroll exports" setting. */
  bankDetailsAllowed: boolean;
}

const BANK_DETAIL_COLUMNS: ColumnKey[] = ['bankName', 'iban', 'labourCard'];

export function isColumnOffered(key: ColumnKey, access: ColumnAccess): boolean {
  if (!access.bankDetailsAllowed && BANK_DETAIL_COLUMNS.includes(key)) return false;
  return access.labourCardApplies || key !== 'labourCard';
}

/** Each preset's columns. The order in the file comes from orderedColumns, so a one-tick edit cannot reshuffle it. */
const PRESET_COLUMNS: Record<ExportPreset, ColumnKey[]> = {
  paySummary: ['name', 'staffNumber', 'department', 'fixedPay', 'variablePay', 'adjustments', 'overtimePay', 'deductions', 'netPay', 'paymentStatus'],
  fullBreakdown: [
    'name', 'staffNumber', 'department', 'position', 'country',
    'fixedPay', 'variablePay', 'adjustments', 'overtimePay', 'deductions', 'netPay',
    'regularOvertimeHours', 'premiumOvertimeHours', 'paymentStatus',
  ],
  bankSheet: ['name', 'staffNumber', 'bankName', 'iban', 'labourCard', 'netPay'],
};

export const PRESETS: ExportPreset[] = ['paySummary', 'fullBreakdown', 'bankSheet'];
export const LAYOUTS: ExportLayout[] = ['register', 'summaryAndRegister', 'oneSheetPerGroup'];
export const GROUPINGS: ExportGroupBy[] = ['none', 'department', 'position', 'country', 'paymentStatus'];
export const SORTS: ExportSortBy[] = ['name', 'staffNumber', 'netPayHighToLow'];

export function presetColumns(preset: ExportPreset, access: ColumnAccess): ColumnKey[] {
  return PRESET_COLUMNS[preset].filter((c) => isColumnOffered(c, access));
}

/** With bank details off, the bank sheet would be a name and an amount, so it is not offered at all. */
export function offeredPresets(access: ColumnAccess): ExportPreset[] {
  return PRESETS.filter((preset) => access.bankDetailsAllowed || preset !== 'bankSheet');
}

/** The preset the ticks currently equal, or null once the user has made them their own. */
export function matchingPreset(columns: ColumnKey[], access: ColumnAccess): ExportPreset | null {
  const ticked = new Set(columns);
  return offeredPresets(access).find((preset) => {
    const wanted = presetColumns(preset, access);
    return wanted.length === ticked.size && wanted.every((c) => ticked.has(c));
  }) ?? null;
}

/**
 * The server keeps the order it is sent, so this is the column order of the file. A sheet with bank details is
 * a payment list, read as who, where, how much, so it ends on the amount however it was ticked.
 */
export function orderedColumns(columns: ColumnKey[]): ColumnKey[] {
  const ticked = new Set(columns);
  const ordered = CANONICAL_ORDER.filter((c) => ticked.has(c));
  const paysToBank = ordered.some((c) => c === 'bankName' || c === 'iban' || c === 'labourCard');
  return paysToBank && ticked.has('netPay') ? [...ordered.filter((c) => c !== 'netPay'), 'netPay'] : ordered;
}

export function defaultOptions(access: ColumnAccess): ExportOptions {
  return {
    columns: presetColumns('paySummary', access),
    layout: 'register',
    groupBy: 'none',
    sortBy: 'name',
    showSubtotals: true,
  };
}

/** One sheet per group has nothing to split on without a grouping; the server refuses it too. */
export function needsGrouping(options: Pick<ExportOptions, 'layout' | 'groupBy'>): boolean {
  return options.layout === 'oneSheetPerGroup' && options.groupBy === 'none';
}

/** Mirrors PayrollTableWorkbookBuilder.FileName, which names the same file in Content-Disposition. */
const LAYOUT_FILE_PART: Record<ExportLayout, string> = {
  register: 'Register',
  summaryAndRegister: 'SummaryAndRegister',
  oneSheetPerGroup: 'OneSheetPerGroup',
};

export function exportFileName(year: number, month: number, layout: ExportLayout): string {
  return `Payroll_${year}-${String(month).padStart(2, '0')}_${LAYOUT_FILE_PART[layout]}.xlsx`;
}

/* ── Last used options, per browser. Choices only: never an employee, a figure or an id. ───────────── */

const STORAGE_KEY = 'payrollTableExport.options.v1';

function oneOf<T extends string>(allowed: T[], value: unknown, fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

export function loadOptions(access: ColumnAccess): ExportOptions {
  const defaults = defaultOptions(access);
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const saved = JSON.parse(raw) as Partial<ExportOptions> | null;
    if (!saved || typeof saved !== 'object') return defaults;
    const kept = Array.isArray(saved.columns)
      ? CANONICAL_ORDER.filter((c) => saved.columns!.includes(c) && isColumnOffered(c, access))
      : [];
    return {
      // The name is always on, and a saved list with nothing else in it is not a real choice.
      columns: kept.length ? Array.from(new Set<ColumnKey>(['name', ...kept])) : defaults.columns,
      layout: oneOf(LAYOUTS, saved.layout, defaults.layout),
      groupBy: oneOf(GROUPINGS, saved.groupBy, defaults.groupBy),
      sortBy: oneOf(SORTS, saved.sortBy, defaults.sortBy),
      showSubtotals: typeof saved.showSubtotals === 'boolean' ? saved.showSubtotals : defaults.showSubtotals,
    };
  } catch {
    return defaults;
  }
}

export function saveOptions(options: ExportOptions): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
  } catch {
    /* Private window or blocked storage: the panel simply starts from its defaults next time. */
  }
}
